// Ferramentas pagas v2 (§4.13 do redesenho): o "Prepare-se com a Empregare"; o pedido da análise (e a volta da página
// pública, com o arquivo guardado no navegador); a página pública; o relatório (acompanhar a IA, liberar com crédito, apagar,
// copiar o texto sugerido, treinar a entrevista) e a gaveta de pagamento, sobre o carrinho e o checkout de hoje
// (PainelProdutos: AdicionarNoCarrinho, FinalizarCompra, ObterValoresParcelamento, ChecarStatusPedido).
import { enviarFormulario, obterJson } from './v2/api';
import { avisar, avisarErro, avisarSucesso } from './v2/aviso';
import { comBotao, ocupado } from './v2/botao';
import { limparErros, MASCARAS, mascarar, mostrarErro, mostrarErros, semAcento, so, validarAoVivo } from './v2/campos';
import { desempilhar, empilhar, irPara } from './v2/sobreposicao';
import { obterUsuario } from './v2/usuario';

const $ = id => document.getElementById(id);
const esperar = ms => new Promise(resolver => setTimeout(resolver, ms));
const urlsDe = el => {
    try {
        return JSON.parse(el?.dataset.urls || '{}');
    } catch {
        return {};
    }
};
const reais = v => new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(Number(v) || 0);

function _erroArquivo(arquivo) {
    if (!arquivo) {
        return 'Escolha o arquivo do seu currículo.';
    }
    if (!/\.(pdf|docx?)$/i.test(arquivo.name || '')) {
        return 'Envie um arquivo PDF ou Word.';
    }
    return arquivo.size > 10000000 ? 'O arquivo pode ter até 10 MB.' : '';
}

// ---------- o arquivo da página pública, guardado no navegador enquanto a pessoa entra ou cria a conta ----------
// IndexedDB guarda o File inteiro (o localStorage só guarda texto). Sem ele (aba anônima antiga, armazenamento bloqueado), a
// pessoa escolhe o arquivo de novo na volta.
const _BANCO = 'empregare-ferramentas';
const _CHAVE = 'analise';
// Mais velho que isso, a volta não usa (a pessoa desistiu e voltou outro dia).
const _VALIDADE_MS = 60 * 60 * 1000;

function _banco() {
    return new Promise((resolver, rejeitar) => {
        const pedido = indexedDB.open(_BANCO, 1);
        pedido.onupgradeneeded = () => pedido.result.createObjectStore('pendente');
        pedido.onsuccess = () => resolver(pedido.result);
        pedido.onerror = () => rejeitar(pedido.error);
    });
}

// Uma transação com prazo: com a cota cheia o IndexedDB aborta (sem o onabort a promessa não terminava e o botão girava).
async function _naLoja(fazer) {
    const db = await _banco();
    let resultado;
    const transacao = new Promise((resolver, rejeitar) => {
        const tx = db.transaction('pendente', 'readwrite');
        resultado = fazer(tx.objectStore('pendente'));
        tx.oncomplete = () => resolver(resultado);
        tx.onerror = () => rejeitar(tx.error);
        tx.onabort = () => rejeitar(tx.error || new Error('abortada'));
    });
    transacao.catch(() => { /* quem perde a corrida não vira erro solto */ });
    let relogio;
    const prazo = new Promise((_, rejeitar) => { relogio = setTimeout(() => rejeitar(new Error('prazo')), 5000); });
    try {
        return await Promise.race([transacao, prazo]);
    } finally {
        clearTimeout(relogio);
        db.close();
    }
}

const _guardarPendente = valor => _naLoja(loja => { loja.put(valor, _CHAVE); });
const _vencido = v => Date.now() - (v?.quando || 0) >= _VALIDADE_MS;

// Lê e apaga: o arquivo serve uma vez só.
async function _tirarPendente() {
    const leitura = await _naLoja(loja => {
        const pedido = loja.get(_CHAVE);
        pedido.onsuccess = () => loja.delete(_CHAVE);
        return pedido;
    });
    return leitura.result && !_vencido(leitura.result) ? leitura.result : null;
}

// O que venceu sai (a pessoa desistiu do cadastro): o currículo não fica no navegador sem prazo.
const _limparVencido = () => _naLoja(loja => {
    const pedido = loja.get(_CHAVE);
    pedido.onsuccess = () => {
        if (pedido.result && _vencido(pedido.result)) {
            loja.delete(_CHAVE);
        }
    };
}).catch(() => { /* sem o armazenamento, não há o que limpar */ });

// ---------- página pública ----------
// O botão do topo abre a escolha do arquivo; escolhido, ele fica guardado no navegador e a pessoa segue para o cadastro (ou
// direto ao pedido, se já entrou), onde confere o arquivo e pode juntar a vaga antes de enviar.
function _paginaPublica(pub) {
    const form = $('frPubForm');
    const arq = $('frPubArq');
    const escolher = form.querySelector('[data-fr-escolher]');
    _limparVencido();

    const seguir = async () => {
        const arquivo = arq.files[0];
        const erro = _erroArquivo(arquivo);
        mostrarErro(arq, erro || '');
        if (erro) {
            escolher.focus();
            return;
        }
        await comBotao(escolher, async () => {
            try {
                await _guardarPendente({ arquivo, nome: arquivo.name, vagaLink: '', quando: Date.now() });
            } catch {
                // Sem o armazenamento, a volta abre o pedido vazio e a pessoa escolhe o arquivo de novo.
            }
            const usuario = await obterUsuario().catch(() => null);
            window.location.href = usuario?.logado ? pub.dataset.retomar : pub.dataset.cadastro;
            return true;
        }, { manter: ok => ok === true });
    };

    // O valor zerado deixa escolher o mesmo arquivo de novo (na volta pelo histórico, o campo ainda o tem e o "change" não viria).
    const abrirEscolha = () => {
        if (ocupado(escolher)) {
            return;
        }
        arq.value = '';
        arq.click();
    };
    escolher.addEventListener('click', abrirEscolha);
    arq.addEventListener('change', () => {
        if (arq.files[0]) {
            seguir();
        }
    });
    form.addEventListener('submit', e => {
        e.preventDefault();
        seguir();
    });

    // A chamada do fim volta ao topo e já abre a escolha do arquivo.
    pub.querySelector('[data-fr-topo]')?.addEventListener('click', e => {
        e.preventDefault();
        form.scrollIntoView({ behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth', block: 'center' });
        escolher.focus({ preventScroll: true });
        abrirEscolha();
    });
}

// ---------- pedido da análise ----------
function _pedido(nova) {
    const urls = urlsDe(nova);
    const form = $('frNovaForm');
    const arq = $('frArq');
    const link = $('frVagaLink');
    const zona = nova.querySelector('[data-fr-zona]');
    const nome = nova.querySelector('[data-fr-nome]');
    const tipoArquivo = form.querySelector('[data-fr-arquivo]');
    const chips = [...form.querySelectorAll('[data-fr-vaga]')];
    let vaga = chips.find(c => c.getAttribute('aria-pressed') === 'true')?.dataset.frVaga || '';
    // O arquivo que veio da página pública (não dá para pôr um File no <input type="file"> em todo navegador).
    let pendente = null;

    const atualizarZona = () => {
        zona.hidden = !tipoArquivo.checked;
    };
    form.querySelectorAll('[name="curriculoTipo"]').forEach(r => r.addEventListener('change', atualizarZona));
    arq.addEventListener('change', () => {
        pendente = null;
        nome.textContent = arq.files[0]?.name || 'Nenhum arquivo escolhido';
        mostrarErro(arq, '');
    });

    const marcar = escolhido => {
        chips.forEach(c => c.setAttribute('aria-pressed', String(c === escolhido)));
        vaga = escolhido?.dataset.frVaga || '';
    };
    chips.forEach(chip => chip.addEventListener('click', () => {
        const ligar = chip.getAttribute('aria-pressed') !== 'true';
        marcar(ligar ? chip : null);
        if (ligar) {
            link.value = '';
            mostrarErro(link, '');
        }
    }));
    link.addEventListener('input', () => {
        if (link.value.trim()) {
            marcar(null);
        }
    });

    const regras = {
        frVagaLink: () => {
            const v = link.value.trim();
            return !v || /^https?:\/\/[^\s.]+\.[^\s]+$/i.test(v) ? '' : 'Cole o link completo da vaga, começando com https://';
        }
    };
    validarAoVivo(form, regras);

    const enviar = async () => {
        limparErros(form);
        const comArquivo = tipoArquivo.checked;
        const arquivo = pendente || arq.files[0];
        const erros = [];
        if (comArquivo) {
            erros.push([arq, _erroArquivo(arquivo)]);
        }
        erros.push([link, regras.frVagaLink()]);
        if (mostrarErros(erros)) {
            return;
        }

        const dados = new FormData();
        dados.append('curriculoTipo', form.querySelector('[name="curriculoTipo"]:checked')?.value || '');
        if (comArquivo) {
            dados.append('arquivo', arquivo, arquivo.name);
        }
        if (vaga) {
            dados.append('vaga', vaga);
        } else if (link.value.trim()) {
            dados.append('vagaLink', link.value.trim());
        }

        await comBotao(form.querySelector('[type="submit"]'), async () => {
            try {
                const r = await enviarFormulario(urls.salvar, dados);
                if (r?.sucesso && r.url) {
                    window.location.href = r.url;
                    return true;
                }
                mostrarErro(null, r?.mensagem || 'Não foi possível começar a análise. Tente de novo.', $('frNovaE'));
            } catch {
                mostrarErro(null, 'Não foi possível começar a análise. Confira a sua internet e tente de novo.', $('frNovaE'));
            }
            return false;
        }, { manter: ok => ok === true });
    };
    form.addEventListener('submit', e => {
        e.preventDefault();
        enviar();
    });

    // Volta do login ou do cadastro da página pública: o arquivo guardado volta escolhido, e a pessoa confere antes de enviar
    // (num navegador dividido, o arquivo pode ser de outra pessoa).
    if (nova.dataset.retomar === '1') {
        _tirarPendente().then(p => {
            if (!(p?.arquivo instanceof Blob)) {
                return;
            }
            pendente = p.arquivo instanceof File ? p.arquivo : new File([p.arquivo], p.nome || 'curriculo.pdf', { type: p.arquivo.type });
            tipoArquivo.checked = true;
            atualizarZona();
            nome.textContent = pendente.name;
            if (p.vagaLink) {
                link.value = p.vagaLink;
            }
            const botao = form.querySelector('[type="submit"]');
            botao.scrollIntoView({ block: 'center' });
            botao.focus({ preventScroll: true });
            avisar(`O arquivo ${pendente.name} voltou escolhido. Confira e toque em "${botao.textContent.trim()}".`);
        }).catch(() => { /* sem o arquivo guardado, a pessoa escolhe de novo */ });
    } else {
        _limparVencido();
    }
}

// ---------- abas (uma por caminho, no plano de carreira) ----------
// chave: o parâmetro do endereço que guarda a aba ("caminho").
function _abas(hub, chave = 'aba') {
    const abas = [...hub.querySelectorAll('[data-fr-abas] [role="tab"]')];
    const escolher = (aba, focar) => {
        abas.forEach(a => {
            const sel = a === aba;
            a.setAttribute('aria-selected', String(sel));
            a.tabIndex = sel ? 0 : -1;
            $(a.getAttribute('aria-controls')).hidden = !sel;
        });
        if (focar) {
            aba.focus();
        }
        // A aba fica no endereço: voltar de uma análise, de um treino ou de uma carta abre a mesma.
        const endereco = new URL(window.location.href);
        endereco.searchParams.set(chave, aba.dataset.aba);
        history.replaceState(history.state, '', endereco);
    };
    abas.forEach((aba, i) => {
        aba.addEventListener('click', () => escolher(aba));
        aba.addEventListener('keydown', e => {
            const alvo = e.key === 'ArrowRight' ? abas[(i + 1) % abas.length]
                : e.key === 'ArrowLeft' ? abas[(i - 1 + abas.length) % abas.length]
                    : e.key === 'Home' ? abas[0]
                        : e.key === 'End' ? abas[abas.length - 1]
                            : null;
            if (alvo) {
                e.preventDefault();
                escolher(alvo, true);
            }
        });
    });
}

// ---------- relatório: acompanhar a IA ----------
// As etapas gravadas (0 a 5) movem a lista e a barra; ao terminar a parte esperada, a página recarrega com o resultado.
function _marcarEtapas(lista, feitas, agora) {
    lista?.querySelectorAll('li').forEach((li, i) => {
        if (i < feitas) {
            li.dataset.s = 'feita';
        } else if (i === agora) {
            li.dataset.s = 'agora';
        } else {
            delete li.dataset.s;
        }
    });
}

async function _acompanhar(rel, urls, situacao) {
    const barra = rel.querySelector('[data-fr-barra]');
    const lista = rel.querySelector('[data-fr-etapas]');
    const status = rel.querySelector('[data-fr-status]');
    const inicio = Date.now();
    let falhas = 0;
    // Com 20 minutos para (o job perdido não termina nunca): a pessoa atualiza quando quiser.
    while (Date.now() - inicio < 20 * 60 * 1000) {
        // Com a aba escondida ou sem conexão, consulta menos.
        await esperar(document.hidden ? 10000 : 3000 * Math.min(5, falhas + 1));
        try {
            const r = await obterJson(urls.status, { semCache: true });
            // A análise sumiu (apagada em outra aba): o servidor diz o porquê e não há mais o que acompanhar. Sem a mensagem, é um
            // erro passageiro: tenta de novo, mais devagar.
            if (!r?.sucesso) {
                if (!r?.mensagem) {
                    falhas += 1;
                    continue;
                }
                if (status) {
                    status.textContent = r.mensagem;
                }
                return;
            }
            falhas = 0;
            if (situacao === 'Processando') {
                if (r.gratisPronta || r.falhou) {
                    window.location.reload();
                    return;
                }
                // Lendo e entendendo andam pelo tempo; o score e o resumo, pelas etapas gravadas.
                const passou = (Date.now() - inicio) / 1000;
                const feitas = r.etapas >= 1 ? 3 : passou > 8 ? 1 : 0;
                _marcarEtapas(lista, feitas, feitas);
                barra.style.width = `${r.etapas >= 1 ? 75 : Math.min(60, 10 + passou * 2)}%`;
            } else {
                if (r.finalizado || r.falhou) {
                    window.location.reload();
                    return;
                }
                const feitas = Math.max(0, (r.etapas || 2) - 2);
                _marcarEtapas(lista, feitas, feitas);
                barra.style.width = `${45 + feitas * 18}%`;
            }
        } catch {
            falhas += 1;
            if (falhas >= 5 && status) {
                status.textContent = 'Sem conexão. Tentando de novo…';
            }
        }
        if (status && Date.now() - inicio > 5 * 60 * 1000) {
            status.textContent = 'Está demorando mais que o normal. Você pode sair: a análise fica guardada em Prepare-se com a Empregare.';
        }
    }
    if (status) {
        status.textContent = 'Está demorando mais que o normal. Atualize a página daqui a pouco: a análise fica guardada em Prepare-se com a Empregare.';
    }
}

// Apagar (a análise, a carta ou o aconselhamento): a confirmação abre numa janela, como o apagar das Mensagens. O foco começa
// no Cancelar; o Voltar do Android, o Esc e o toque fora fecham sem apagar.
function _ligarApagar(raiz, urlExcluir, id) {
    const dlg = document.getElementById('frApagarDlg');
    if (!dlg) {
        return;
    }
    let origem = null;
    raiz.querySelectorAll('[data-fr-apagar]').forEach(botao => botao.addEventListener('click', () => {
        origem = botao;
        dlg.showModal();
        empilhar('v2Apagar', () => dlg.close());
        dlg.querySelector('[data-fr-apagar-nao]').focus();
    }));
    let toqueNoFundo = false;
    dlg.addEventListener('pointerdown', e => { toqueNoFundo = e.target === dlg; });
    dlg.addEventListener('click', e => {
        if ((e.target === dlg && toqueNoFundo && e.detail < 2) || e.target.closest('[data-fr-apagar-nao]')) {
            dlg.close();
        }
    });
    dlg.addEventListener('close', () => {
        desempilhar('v2Apagar');
        origem?.focus();
    });
    dlg.querySelector('[data-fr-apagar-sim]').addEventListener('click', e => comBotao(e.currentTarget, async () => {
        const dados = new FormData();
        dados.append('id', id);
        try {
            const r = await enviarFormulario(urlExcluir, dados);
            if (r?.sucesso) {
                // Sai de dentro da janela: o irPara troca a entrada dela no histórico pela página nova.
                irPara(r.url);
                return true;
            }
            avisarErro(r?.mensagem || 'Não foi possível apagar agora. Tente de novo.');
        } catch {
            avisarErro('Não foi possível apagar agora. Confira a sua internet e tente de novo.');
        }
        return false;
    }, { manter: ok => ok === true }));
}

function _ligarCopiar(raiz) {
    raiz.querySelectorAll('[data-fr-copiar-texto]').forEach(botao => botao.addEventListener('click', async () => {
        try {
            await navigator.clipboard.writeText(botao.dataset.frCopiarTexto);
            avisarSucesso('Texto copiado.');
        } catch {
            avisarErro('Não foi possível copiar. Selecione o texto e copie.');
        }
    }));
}

// O PDF do relatório leva alguns segundos para ser gerado no servidor: baixado por fetch, o link fica ocupado (sem 2º pedido)
// e, quando o servidor devolve a página em vez do PDF (falha na geração), a pessoa é avisada.
// O navegador dentro de app (Instagram, Facebook, LinkedIn, WebView do Android) ignora o download por blob: lá o link abre direto.
const _navegadorDeApp = /FBAN|FBAV|Instagram|LinkedInApp|\bLine\/|; wv\)/i.test(navigator.userAgent);

// A nota do relatório do simulador sobe de 0 ao valor junto com o anel (CSS: sr-anel). No PDF e sem animação ela já nasce pronta.
function _contarNota(raiz) {
    const texto = raiz.querySelector('.sr-nota b')?.firstChild;
    const alvo = parseInt(texto?.textContent, 10);
    if (!alvo || document.documentElement.classList.contains('pdf-v2') || matchMedia('(prefers-reduced-motion: reduce)').matches) {
        return;
    }
    texto.textContent = '0';
    const inicio = performance.now() + 350;
    const passo = (agora) => {
        const t = Math.min(1, Math.max(0, (agora - inicio) / 1100));
        texto.textContent = Math.round(alvo * (1 - (1 - t) ** 3));
        if (t < 1) {
            requestAnimationFrame(passo);
        }
    };
    requestAnimationFrame(passo);
}

function _ligarPdf(raiz) {
    raiz.querySelectorAll('[data-fr-pdf]').forEach(link => link.addEventListener('click', e => {
        e.preventDefault();
        if (_navegadorDeApp) {
            location.assign(link.href);
            return;
        }
        comBotao(link, async () => {
            try {
                const r = await fetch(link.href, { credentials: 'same-origin' });
                if (!r.ok || !(r.headers.get('content-type') || '').includes('pdf')) {
                    throw new Error('pdf');
                }
                const nome = /filename\*?=(?:UTF-8'')?"?([^";]+)/i.exec(r.headers.get('content-disposition') || '')?.[1] || 'relatorio.pdf';
                const url = URL.createObjectURL(await r.blob());
                const baixar = document.createElement('a');
                baixar.href = url;
                try {
                    baixar.download = decodeURIComponent(nome);
                } catch {
                    baixar.download = nome;
                }
                document.body.append(baixar);
                baixar.click();
                baixar.remove();
                setTimeout(() => URL.revokeObjectURL(url), 10000);
            } catch {
                avisarErro('Não foi possível gerar o PDF agora. Tente de novo em instantes.');
            }
        });
    }));
}

// Liberar com crédito (o que já tinha ou o que o pagamento acabou de trazer). tentativas: o crédito do pagamento entra junto da
// confirmação, mas a leitura do saldo pode chegar um instante antes.
async function _liberar(urls, id, tentativas = 1) {
    let ultima = null;
    for (let i = 0; i < tentativas; i += 1) {
        const dados = new FormData();
        dados.append('id', id);
        try {
            ultima = await enviarFormulario(urls.liberar, dados);
        } catch (erro) {
            // Uma falha de rede logo depois do pagamento não pode acabar com as tentativas que sobraram.
            if (i === tentativas - 1) {
                throw erro;
            }
            ultima = null;
        }
        if (ultima?.sucesso) {
            return ultima;
        }
        await esperar(2000);
    }
    return ultima;
}

// Liberando: o cartão "Estamos terminando" fica abaixo do resumo grátis, fora da tela de quem acabou de liberar. Leva até ele e o destaca.
function _mostrarAndamento(rel) {
    const card = rel.querySelector('[data-fr-etapas]')?.closest('.fr-card');
    if (!card) {
        return;
    }
    const ir = () => requestAnimationFrame(() => {
        card.scrollIntoView({ behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth', block: 'center' });
        card.classList.add('fr-card--chegou');
        const titulo = card.querySelector('h2');
        titulo.tabIndex = -1;
        titulo.focus({ preventScroll: true });
    });
    // Só depois do load: ao recarregar, o navegador devolve a rolagem de antes e atropelaria a nossa.
    if (document.readyState === 'complete') {
        ir();
    } else {
        window.addEventListener('load', ir, { once: true });
    }
}

function _relatorio(rel) {
    const urls = urlsDe(rel);
    const id = rel.dataset.id;
    const situacao = rel.dataset.situacao;

    if (situacao === 'Processando' || situacao === 'Liberando') {
        _acompanhar(rel, urls, situacao);
    }
    if (situacao === 'Liberando') {
        _mostrarAndamento(rel);
    }
    _ligarPdf(rel);

    rel.querySelectorAll('[data-fr-liberar]').forEach(botao => botao.addEventListener('click', () => comBotao(botao, async () => {
        const erro = $('frLiberarE');
        try {
            const r = await _liberar(urls, id);
            if (r?.sucesso) {
                window.location.reload();
                return true;
            }
            mostrarErro(null, r?.mensagem || 'Não foi possível liberar agora. Tente de novo.', erro);
        } catch {
            mostrarErro(null, 'Não foi possível liberar agora. Confira a sua internet e tente de novo.', erro);
        }
        return false;
    }, { manter: ok => ok === true })));

    _ligarApagar(rel, urls.excluir, id);
    _ligarCopiar(rel);

    rel.querySelector('[data-fr-simulacao]')?.addEventListener('click', e => comBotao(e.currentTarget, async () => {
        const dados = new FormData();
        dados.append('id', id);
        try {
            const r = await enviarFormulario(urls.simulacao, dados);
            if (r?.sucesso && r.url) {
                window.location.href = r.url;
                return true;
            }
            mostrarErro(null, r?.mensagem || 'Não foi possível começar o treino. Tente de novo.', $('frSimE'));
        } catch {
            mostrarErro(null, 'Não foi possível começar o treino. Confira a sua internet e tente de novo.', $('frSimE'));
        }
        return false;
    }, { manter: ok => ok === true }));

    _aplicarNoCurriculo(rel, urls, id);

    const gaveta = $('frPag');
    if (gaveta) {
        const abrir = _gaveta(gaveta, {
            // Pagou: libera a análise e acompanha as etapas 3 a 5 na própria gaveta.
            aoConfirmar: async ({ texto, barra }) => {
                const r = await _liberar(urls, id, 5).catch(() => null);
                if (!r?.sucesso) {
                    texto.textContent = 'O pagamento entrou, mas o relatório ainda não começou. Recarregue a página: o seu crédito está guardado.';
                    return;
                }
                const inicio = Date.now();
                while (Date.now() - inicio < 20 * 60 * 1000) {
                    await esperar(document.hidden ? 10000 : 3000);
                    const s = await obterJson(urls.status, { semCache: true }).catch(() => null);
                    if (s?.sucesso) {
                        barra.style.width = `${Math.min(100, 20 + Math.max(0, (s.etapas || 2) - 2) * 27)}%`;
                        if (s.finalizado || s.falhou) {
                            // Com a gaveta aberta, o irPara troca a entrada dela: o Voltar da página nova não cai nela.
                            irPara(`${window.location.pathname}${s.finalizado ? '?pago=1' : ''}`);
                            return;
                        }
                    }
                }
                texto.textContent = 'O relatório está demorando mais que o normal. Atualize a página daqui a pouco: ele fica guardado em Prepare-se com a Empregare.';
            }
        });
        rel.querySelectorAll('[data-fr-comprar]').forEach(botao => botao.addEventListener('click', () => abrir(botao)));
    }
}

// ---------- relatório completo: "Aplicar no meu currículo" (a camada por cima do relatório) ----------
function _aplicarNoCurriculo(rel, urls, id) {
    let aplicadas = 0;
    // A referência antes da 1ª troca: o texto novo substitui o <span> marcado.
    const texto = rel.querySelector('[data-fr-cont]')?.parentElement;
    const contar = () => {
        aplicadas += 1;
        if (texto) {
            texto.textContent = `${aplicadas} ${aplicadas === 1 ? 'mudança aplicada' : 'mudanças aplicadas'} no seu currículo. Veja o novo score.`;
        }
    };
    const falhou = 'Não foi possível aplicar agora. Confira a sua internet e tente de novo.';

    rel.querySelectorAll('[data-fr-aplicar-apresentacao]').forEach(botao => botao.addEventListener('click', () => comBotao(botao, async () => {
        const dados = new FormData();
        dados.append('id', id);
        dados.append('indice', botao.dataset.frAplicarApresentacao);
        try {
            const r = await enviarFormulario(urls.aplicarApresentacao, dados);
            if (r?.sucesso) {
                const aviso = botao.closest('[data-fr-otimizacao]').querySelector('[data-fr-aplicado]');
                aviso.hidden = false;
                aviso.tabIndex = -1;
                botao.hidden = true;
                aviso.focus();
                contar();
                return;
            }
            avisarErro(r?.mensagem || falhou);
        } catch {
            avisarErro(falhou);
        }
    })));

    const camada = rel.querySelector('[data-fr-trajetoria]');
    const pedir = camada?.querySelector('[data-fr-sugerir-trajetoria]');
    pedir?.addEventListener('click', () => comBotao(pedir, async () => {
        const erro = $('frTrajE');
        mostrarErro(null, '', erro);
        const dados = new FormData();
        dados.append('id', id);
        try {
            const r = await enviarFormulario(urls.sugerirTrajetoria, dados);
            if (!r?.sucesso || !r.itens?.length) {
                mostrarErro(null, r?.mensagem || 'Não foi possível falar com a IA agora. Tente de novo em instantes.', erro);
                return;
            }
            _desenharCorrecoes(camada.querySelector('[data-fr-correcoes]'), r.itens, urls, contar);
            pedir.hidden = true;
            camada.querySelector('.fr-corr textarea')?.focus();
        } catch {
            mostrarErro(null, 'Não foi possível falar com a IA agora. Confira a sua internet e tente de novo.', erro);
        }
    }));

    const novo = rel.querySelector('[data-fr-novo-score]');
    novo?.addEventListener('click', () => comBotao(novo, async () => {
        const dados = new FormData();
        dados.append('id', id);
        try {
            const r = await enviarFormulario(urls.novoScore, dados);
            if (r?.sucesso && r.url) {
                window.location.href = r.url;
                return true;
            }
            mostrarErro(null, r?.mensagem || 'Não foi possível pedir o novo score agora. Tente de novo.', $('frNovoE'));
        } catch {
            mostrarErro(null, 'Não foi possível pedir o novo score agora. Confira a sua internet e tente de novo.', $('frNovoE'));
        }
        return false;
    }, { manter: ok => ok === true }));
}

// Antes × depois de cada experiência, com a sugestão editável. O texto entra por textContent/value (vem da IA e do currículo).
function _desenharCorrecoes(lista, itens, urls, contar) {
    const el = (tag, classe, texto) => {
        const e = document.createElement(tag);
        if (classe) {
            e.className = classe;
        }
        if (texto != null) {
            e.textContent = texto;
        }
        return e;
    };
    lista.replaceChildren(...itens.map((item, i) => {
        const card = el('div', 'fr-corr');
        const antes = el('div', 'fr-antes');
        antes.append(el('small', null, 'Hoje'), document.createTextNode(item.antes || ''));
        const rotulo = el('label', null, 'Sugestão da IA (você pode editar)');
        rotulo.htmlFor = `frCorr${i}`;
        rotulo.style.fontWeight = '700';
        rotulo.style.color = 'var(--c-navy)';
        const area = el('textarea');
        area.id = `frCorr${i}`;
        area.maxLength = 1500;
        area.value = item.depois || '';
        area.setAttribute('aria-describedby', `frCorr${i}D frCorr${i}E`);
        const dica = el('p', 'fr-nota', '[quanto?] marca um número que só você sabe: troque pelo seu ou apague antes de aplicar.');
        dica.id = `frCorr${i}D`;
        dica.hidden = !area.value.includes('[quanto?]');
        const erro = el('p', 'campo-erro');
        erro.id = `frCorr${i}E`;
        erro.setAttribute('role', 'alert');
        erro.hidden = true;
        erro.innerHTML = '<svg class="ico" aria-hidden="true"><use href="#i-info-circle"></use></svg><span></span>';
        const feito = el('p', 'fr-nota');
        feito.dataset.frFeito = '';
        feito.tabIndex = -1;
        feito.hidden = true;
        const acoes = el('div', 'fr-acoes');
        const aplicar = el('button', 'btn btn--sm btn--pri', 'Aplicar no meu currículo');
        aplicar.type = 'button';
        const manter = el('button', 'fr-link', 'Manter o meu');
        manter.type = 'button';
        acoes.append(aplicar, manter);

        const fechar = texto => {
            acoes.hidden = true;
            area.readOnly = true;
            dica.hidden = true;
            feito.textContent = texto;
            feito.hidden = false;
            feito.focus();
        };
        area.addEventListener('input', () => {
            dica.hidden = !area.value.includes('[quanto?]');
            if (area.getAttribute('aria-invalid') === 'true') {
                mostrarErro(area, '');
            }
        });
        aplicar.addEventListener('click', () => comBotao(aplicar, async () => {
            const texto = area.value.trim();
            if (texto.includes('[quanto?]')) {
                mostrarErro(area, 'Troque o [quanto?] pelo seu número ou apague antes de aplicar.');
                area.focus();
                return;
            }
            const dados = new FormData();
            dados.append('experiencia', item.id);
            dados.append('texto', texto);
            try {
                const r = await enviarFormulario(urls.aplicarExperiencia, dados);
                if (r?.sucesso) {
                    card.dataset.feita = 'sim';
                    fechar('Aplicado no seu currículo.');
                    contar();
                    return;
                }
                mostrarErro(area, r?.mensagem || 'Não foi possível aplicar agora. Tente de novo.');
            } catch {
                mostrarErro(area, 'Não foi possível aplicar agora. Confira a sua internet e tente de novo.');
            }
        }));
        manter.addEventListener('click', () => fechar('Mantido o seu texto de hoje.'));

        card.append(el('h5', null, item.titulo || ''), antes, rotulo, area, dica, erro, acoes, feito);
        return card;
    }));
}

// ---------- gaveta de pagamento ----------
function _cpfValido(v) {
    const c = so(v);
    if (c.length !== 11 || /^(\d)\1{10}$/.test(c)) {
        return false;
    }
    const digito = n => {
        let soma = 0;
        for (let i = 0; i < n; i += 1) {
            soma += Number(c[i]) * (n + 1 - i);
        }
        const resto = (soma * 10) % 11;
        return resto === 10 ? 0 : resto;
    };
    return digito(9) === Number(c[9]) && digito(10) === Number(c[10]);
}

function _luhn(v) {
    const d = so(v);
    if (d.length < 13 || d.length > 19) {
        return false;
    }
    let soma = 0;
    for (let i = 0; i < d.length; i += 1) {
        let n = Number(d[d.length - 1 - i]);
        if (i % 2) {
            n *= 2;
            if (n > 9) {
                n -= 9;
            }
        }
        soma += n;
    }
    return soma % 10 === 0;
}

// A bandeira pelo número (os IDs de método de pagamento do gateway, os mesmos do carrinho de hoje). Elo e Hipercard antes de
// Visa e Discover: os BINs deles começam com 4 e 6.
function _bandeira(numero) {
    const n = so(numero);
    if (/^(636368|438935|504175|451416|636297)/.test(n) || /^50/.test(n)) {
        return 16;
    }
    if (/^606282/.test(n)) {
        return 20;
    }
    if (/^4/.test(n)) {
        return 3;
    }
    if (/^(5[1-5]|222[1-9]|22[3-9]|2[3-6]|27[0-1]|2720)/.test(n)) {
        return 4;
    }
    if (/^3[47]/.test(n)) {
        return 5;
    }
    if (/^6(011|5|4[4-9])/.test(n)) {
        return 15;
    }
    if (/^35(2[89]|[3-8][0-9])/.test(n)) {
        return 19;
    }
    return null;
}

function _validadeErro(v) {
    const m = /^(\d{2})\/(\d{2})$/.exec(v.trim());
    if (!m || Number(m[1]) < 1 || Number(m[1]) > 12) {
        return 'Informe a validade como no cartão (MM/AA).';
    }
    const hoje = new Date();
    const ano = 2000 + Number(m[2]);
    return ano < hoje.getFullYear() || (ano === hoje.getFullYear() && Number(m[1]) < hoje.getMonth() + 1) ? 'Este cartão está vencido.' : '';
}

function _gaveta(dlg, { aoConfirmar }) {
    const urls = urlsDe(dlg);
    const form = $('frPagForm');
    const campos = $('frPagCampos');
    const resumoDados = dlg.querySelector('[data-fr-resumo-dados]');
    const cartao = dlg.querySelector('[data-fr-cartao]');
    const pagar = dlg.querySelector('[data-fr-pagar]');
    const rotuloPagar = dlg.querySelector('[data-fr-pagar-t]');
    const titular = dlg.querySelector('[data-fr-titular-t]');
    const resumoTexto = dlg.querySelector('[data-fr-resumo-texto]');
    const formas = form.querySelector('.fr-formas')?.closest('fieldset');
    const linhaDesconto = dlg.querySelector('[data-fr-desconto-l]');
    const cupomAbrir = dlg.querySelector('[data-fr-cupom-abrir]');
    const cupomCaixa = $('frCupomC');
    const cupomErro = $('frCupomE');
    const erroGeral = $('frPagE');
    const passos = [...dlg.querySelectorAll('[data-passo]')];
    let gatilho = null;
    let bandeira = null;
    let confirmado = false;
    // "Alterar" aberto: os campos ficam à vista mesmo trocando a forma de pagar.
    let alterando = false;
    let cupomAplicado = '';
    // Cupom de 100%: não há o que cobrar, e o pedido sai sem o cartão (o servidor aprova direto).
    let totalZero = false;
    // Cada pedido gerado na gaveta é acompanhado até o fim: o Pix de antes de "Alterar" continua valendo e, pago, libera.
    const acompanhando = new Set();
    const cartoesEmAnalise = new Set();
    let pedidoAtual = null;

    // Com focar, o foco vai ao passo novo (o leitor de tela anuncia a troca).
    const mostrar = (nome, focar) => passos.forEach(p => {
        p.hidden = p.dataset.passo !== nome;
        if (focar && !p.hidden) {
            p.tabIndex = -1;
            p.focus({ preventScroll: true });
        }
    });
    const forma = () => form.querySelector('[name="frForma"]:checked')?.value || 'Pix';
    const ehCartao = () => forma() === 'CartaoCredito' && !totalZero;
    const valor = id => $(id).value.trim();

    const atualizarBotao = () => {
        const total = dlg.querySelector('[data-fr-total]').textContent;
        rotuloPagar.textContent = totalZero ? 'Liberar com o cupom' : ehCartao() ? `Pagar ${total} no cartão` : `Gerar o Pix de ${total}`;
    };

    // Só os dados da forma escolhida: o Pix da C6 pede o CPF; o cartão, também o celular e o endereço (o antifraude da Vindi).
    const ajustarForma = () => {
        const cartaoAgora = ehCartao();
        cartao.hidden = !cartaoAgora;
        if (formas) {
            formas.hidden = totalZero;
        }
        dlg.querySelectorAll('[data-fr-so-cartao]').forEach(el => { el.hidden = !cartaoAgora; });
        titular.textContent = cartaoAgora ? titular.dataset.cartao : titular.dataset.pix;
        const resumo = cartaoAgora ? form.dataset.frResumoCartao : form.dataset.frResumoPix;
        const fechado = Boolean(resumo) && !alterando;
        resumoTexto.textContent = resumo || '';
        resumoDados.hidden = !fechado;
        campos.hidden = fechado;
        atualizarBotao();
    };

    // O pedido como o carrinho calculou: o desconto do cupom, quando há, e o total.
    const mostrarCarrinho = d => {
        const desconto = Number(d.ValorDesconto) || 0;
        cupomAplicado = desconto > 0 ? d.CupomCodigo || '' : '';
        dlg.querySelector('[data-fr-subtotal]').textContent = reais(d.ValorSubtotal ?? d.ValorTotal);
        dlg.querySelector('[data-fr-cupom-cod]').textContent = cupomAplicado;
        dlg.querySelector('[data-fr-desconto]').textContent = `− ${reais(desconto)}`;
        linhaDesconto.hidden = !cupomAplicado;
        cupomAbrir.hidden = Boolean(cupomAplicado) || !cupomCaixa.hidden;
        dlg.querySelectorAll('[data-fr-total]').forEach(el => { el.textContent = reais(d.ValorTotal); });
        totalZero = Number(d.ValorTotal) === 0;
        ajustarForma();
    };

    // O CPF e o CEP da conta chegam sem máscara.
    $('frPagCpf').value = MASCARAS.cpf($('frPagCpf').value);
    $('frPagCep').value = MASCARAS.cep($('frPagCep').value);
    mascarar($('frPagCpf'), 'cpf');
    mascarar($('frPagCep'), 'cep');
    $('frPagCel').addEventListener('input', e => {
        const d = so(e.target.value).slice(0, 11);
        e.target.value = d.length > 10 ? d.replace(/^(\d{2})(\d{5})(\d{0,4})$/, '($1) $2-$3') : d.replace(/^(\d{2})(\d{0,4})(\d{0,4})$/, (t, a, b, c) => (b ? `(${a}) ${b}${c ? `-${c}` : ''}` : a));
    });
    $('frCartNum').addEventListener('input', e => {
        e.target.value = so(e.target.value).slice(0, 19).replace(/(\d{4})(?=\d)/g, '$1 ');
    });
    $('frCartVal').addEventListener('input', e => {
        e.target.value = so(e.target.value).slice(0, 4).replace(/^(\d{2})(\d)/, '$1/$2');
    });
    $('frCartCvv').addEventListener('input', e => {
        e.target.value = so(e.target.value).slice(0, 4);
    });

    const soCartao = regra => () => (ehCartao() ? regra() : '');
    const regras = {
        frPagCpf: () => (_cpfValido(valor('frPagCpf')) ? '' : 'Informe um CPF válido.'),
        frPagCel: soCartao(() => (so(valor('frPagCel')).length >= 10 ? '' : 'Informe o celular com DDD.')),
        frPagCep: soCartao(() => (so(valor('frPagCep')).length === 8 ? '' : 'Informe o CEP com 8 números.')),
        frPagNum: soCartao(() => (valor('frPagNum') ? '' : 'Informe o número.')),
        frPagRua: soCartao(() => (valor('frPagRua') ? '' : 'Informe a rua.')),
        frPagBairro: soCartao(() => (valor('frPagBairro') ? '' : 'Informe o bairro.')),
        frPagCidade: soCartao(() => (valor('frPagCidade') ? '' : 'Informe a cidade.')),
        frPagUf: soCartao(() => (valor('frPagUf') ? '' : 'Escolha o estado.')),
        frCartNum: soCartao(() => (_luhn(valor('frCartNum')) && _bandeira(valor('frCartNum')) ? '' : 'Confira o número do cartão.')),
        frCartNome: soCartao(() => (/\S+\s+\S+/.test(valor('frCartNome')) ? '' : 'Informe o nome como está no cartão.')),
        frCartVal: soCartao(() => _validadeErro(valor('frCartVal'))),
        frCartCvv: soCartao(() => (/^\d{3,4}$/.test(valor('frCartCvv')) ? '' : 'Informe os 3 ou 4 números do código de segurança.'))
    };
    validarAoVivo(form, regras, { frPagCpf: v => so(v).length === 11, frPagCep: v => so(v).length === 8 });

    // O CEP completo preenche o endereço (a mesma busca do carrinho de hoje).
    let ultimoCep = '';
    $('frPagCep').addEventListener('input', async e => {
        const cep = so(e.target.value);
        if (cep.length !== 8 || cep === ultimoCep) {
            return;
        }
        ultimoCep = cep;
        try {
            const r = await obterJson(`${urls.cep}?cep=${cep}`, { semCache: true });
            if (r?.sucesso && r.dados) {
                $('frPagRua').value = r.dados.Rua || $('frPagRua').value;
                $('frPagBairro').value = r.dados.Bairro || $('frPagBairro').value;
                $('frPagCidade').value = r.dados.Cidade || $('frPagCidade').value;
                if (r.dados.Estado) {
                    $('frPagUf').value = r.dados.Estado;
                }
                $('frPagNum').focus();
            }
        } catch {
            // Sem a busca, a pessoa preenche o endereço.
        }
    });

    // Mexeu num campo: o resumo (os dados da conta) não volta a esconder o que foi digitado.
    campos.addEventListener('input', () => {
        alterando = true;
    });
    dlg.querySelector('[data-fr-alterar]')?.addEventListener('click', e => {
        alterando = true;
        campos.hidden = false;
        resumoDados.hidden = true;
        e.currentTarget.setAttribute('aria-expanded', 'true');
        $('frPagCpf').focus();
    });

    // Uma troca de carrinho por vez: cada AdicionarNoCarrinho limpa e recria o carrinho no servidor, e duas juntas perdem o cupom.
    let fila = Promise.resolve();
    const naFila = tarefa => {
        fila = fila.catch(() => null).then(tarefa);
        return fila;
    };

    // Põe o produto no carrinho (o de hoje: limpa e põe de novo, mantendo o cupom) e mostra o total dele.
    const refazerCarrinho = async () => {
        const cupomAntes = cupomAplicado;
        const dados = new FormData();
        dados.append('quantidade', '1');
        dados.append('produtoID', dlg.dataset.produto);
        dados.append('origem', dlg.dataset.origem || '');
        const r = await enviarFormulario(urls.adicionar, dados);
        if (!r?.sucesso) {
            const erro = new Error(r?.mensagem || 'carrinho');
            erro.paraPessoa = Boolean(r?.mensagem);
            throw erro;
        }
        let c = await obterJson(urls.carrinho, { semCache: true }).catch(() => null);
        // O Pix gerado antes fechou o carrinho junto com o cupom: o carrinho novo tenta o cupom de novo (o limite de uso decide).
        if (cupomAntes && c?.sucesso && c.dados && !(Number(c.dados.ValorDesconto) > 0)) {
            const comCupom = new FormData();
            comCupom.append('cupom', cupomAntes);
            const rc = await enviarFormulario(urls.cupom, comCupom).catch(() => null);
            if (rc?.sucesso) {
                c = await obterJson(urls.carrinho, { semCache: true }).catch(() => null);
            }
        }
        if (c?.sucesso && c.dados) {
            mostrarCarrinho(c.dados);
        }
    };

    const porNoCarrinho = () => naFila(refazerCarrinho);

    // O cupom vale para o produto do carrinho: ele entra primeiro, e o AdicionarNoCarrinho mantém o cupom nas trocas seguintes.
    const trocarCupom = codigo => naFila(async () => {
        await refazerCarrinho();
        const dados = new FormData();
        dados.append('cupom', codigo);
        const r = await enviarFormulario(urls.cupom, dados);
        if (!r?.sucesso) {
            throw Object.assign(new Error(r?.mensagem || ''), { paraPessoa: Boolean(r?.mensagem) });
        }
        const c = await obterJson(urls.carrinho, { semCache: true });
        if (c?.sucesso && c.dados) {
            mostrarCarrinho(c.dados);
        }
        if (bandeira) {
            carregarParcelas();
        }
    });

    cupomAbrir.addEventListener('click', () => {
        cupomCaixa.hidden = false;
        cupomAbrir.hidden = true;
        cupomAbrir.setAttribute('aria-expanded', 'true');
        $('frCupom').focus();
    });
    const aplicarCupom = dlg.querySelector('[data-fr-cupom-aplicar]');
    aplicarCupom.addEventListener('click', () => comBotao(aplicarCupom, async () => {
        mostrarErro($('frCupom'), '', cupomErro);
        const codigo = valor('frCupom');
        if (!codigo) {
            mostrarErro($('frCupom'), 'Informe o código do cupom.', cupomErro);
            return;
        }
        try {
            await trocarCupom(codigo);
            $('frCupom').value = '';
            cupomCaixa.hidden = true;
            cupomAbrir.setAttribute('aria-expanded', 'false');
            avisarSucesso('Cupom aplicado.');
            pagar.focus();
        } catch (erro) {
            mostrarErro($('frCupom'), erro?.paraPessoa ? erro.message : 'Não foi possível aplicar o cupom agora. Tente de novo.', cupomErro);
        }
    }));
    // O Enter no código aplica o cupom, e não paga.
    $('frCupom').addEventListener('keydown', e => {
        if (e.key === 'Enter') {
            e.preventDefault();
            aplicarCupom.click();
        }
    });
    const tirarCupom = dlg.querySelector('[data-fr-cupom-tirar]');
    tirarCupom.addEventListener('click', () => comBotao(tirarCupom, async () => {
        try {
            await trocarCupom('');
            cupomAbrir.focus();
        } catch {
            avisarErro('Não foi possível tirar o cupom agora. Tente de novo.');
        }
    }));

    const carregarParcelas = async () => {
        const select = $('frCartParc');
        if (!bandeira) {
            return;
        }
        try {
            const r = await obterJson(urls.parcelas, { semCache: true });
            const opcoes = (r?.dados || []).find(x => Number(x.IdMetodoPagamento) === bandeira)?.Parcelamentos || [];
            if (!opcoes.length) {
                return;
            }
            select.replaceChildren(...opcoes.map(o => {
                const semJuros = Number(o.TaxaParcelamento) === 0;
                const texto = `${o.Parcela} ${o.Parcela === 1 ? 'vez' : 'vezes'} de ${reais(o.ValorParcela)}${semJuros ? '' : ` (total de ${reais(o.ValorTransacao)})`}`;
                return new Option(texto, o.Parcela);
            }));
        } catch {
            // Sem as parcelas, fica a opção de 1 vez.
        }
    };

    let carrinhoPronto = null;
    form.querySelectorAll('[name="frForma"]').forEach(r => r.addEventListener('change', () => {
        ajustarForma();
        mostrarErro(null, '', erroGeral);
        if (ehCartao()) {
            // As parcelas vêm do carrinho aberto: escolher o cartão já é a intenção de pagar.
            carrinhoPronto ??= porNoCarrinho().catch(() => { carrinhoPronto = null; });
        }
    }));
    $('frCartNum').addEventListener('input', async () => {
        const nova = _bandeira(valor('frCartNum'));
        if (nova && nova !== bandeira && so(valor('frCartNum')).length >= 6) {
            bandeira = nova;
            // As parcelas são as do carrinho aberto: ele precisa ser o do produto da gaveta (o kit troca o produto).
            await (carrinhoPronto ??= porNoCarrinho().catch(() => { carrinhoPronto = null; }));
            carregarParcelas();
        }
    });

    const confirmar = async () => {
        if (confirmado) {
            return;
        }
        confirmado = true;
        if (!dlg.open) {
            dlg.showModal();
            empilhar('v2Pagamento', () => dlg.close());
        }
        mostrar('ok', true);
        await aoConfirmar({ texto: dlg.querySelector('[data-fr-ok-texto]'), barra: dlg.querySelector('[data-fr-ok-barra]') });
    };

    const recusado = texto => {
        $('frCartCvv').value = '';
        mostrar('dados');
        mostrarErro(null, texto, erroGeral);
        pagar.focus();
    };

    // A situação do pedido até o banco responder (o Pix pode ser pago no app e a pessoa voltar depois). Para em uma hora: pago
    // depois disso, o crédito aparece ao recarregar a página ("Usar 1 crédito").
    const acompanharPedido = async (pedidoID, intervalo) => {
        if (acompanhando.has(pedidoID)) {
            return;
        }
        acompanhando.add(pedidoID);
        const inicio = Date.now();
        let falhas = 0;
        while (!confirmado && Date.now() - inicio < 60 * 60 * 1000) {
            const lento = document.hidden || Date.now() - inicio > 10 * 60 * 1000;
            await esperar(lento ? 15000 : intervalo * Math.min(4, falhas + 1));
            if (confirmado) {
                break;
            }
            try {
                const r = await obterJson(`${urls.status}?pedidoID=${encodeURIComponent(pedidoID)}`, { semCache: true });
                // Um erro do servidor não encerra: o Pix pode ser pago a qualquer momento dentro da hora.
                if (!r?.sucesso) {
                    falhas += 1;
                    continue;
                }
                falhas = 0;
                if (r.situacao === 'Pago') {
                    confirmar();
                    break;
                }
                // Em monitoramento (antifraude) ainda pode ser aprovado; o resto (cancelado, reprovado, contestação) não paga mais.
                if (r.situacao !== 'AguardandoPagamento' && r.situacao !== 'EmMonitoramento') {
                    if (pedidoID === pedidoAtual) {
                        recusado('O pagamento não foi aprovado. Confira os dados ou escolha outra forma de pagar.');
                    }
                    break;
                }
            } catch {
                falhas += 1;
            }
        }
        acompanhando.delete(pedidoID);
        cartoesEmAnalise.delete(pedidoID);
    };

    // O cartão costuma responder em segundos. Sem resposta, a pessoa volta aos dados (o pedido segue acompanhado).
    const esperarCartao = async pedidoID => {
        await esperar(90000);
        if (!confirmado && pedidoID === pedidoAtual && passos.find(p => !p.hidden)?.dataset.passo === 'cartao') {
            // Sem sugerir outra forma de pagar: o cartão em análise ainda pode ser aprovado, e pagar de novo cobraria duas vezes.
            recusado('O banco ainda está analisando o pagamento do cartão. Quando aprovar, a compra libera aqui sozinha. Para não pagar duas vezes, espere a resposta antes de tentar de novo.');
        }
    };

    const mostrarPix = async d => {
        const codigo = d.PixCodigo || '';
        dlg.querySelector('[data-fr-pix]').textContent = codigo;
        dlg.querySelector('[data-fr-validade]').textContent = d.DataMaximaParaEsperarPagamento ? ` Este Pix vale até ${d.DataMaximaParaEsperarPagamento}.` : '';
        const qr = dlg.querySelector('[data-fr-qr]');
        let imagem = d.PixQRCodeUrlImagem;
        if (!imagem && codigo) {
            try {
                const { default: qrcode } = await import(/* webpackChunkName: "qrcode-generator" */ 'qrcode-generator');
                const gerador = qrcode(0, 'L');
                gerador.addData(codigo);
                gerador.make();
                imagem = gerador.createDataURL(4, 8);
            } catch {
                imagem = '';
            }
        }
        qr.hidden = !imagem;
        if (imagem) {
            qr.src = imagem;
        }
        mostrar('pix');
        dlg.querySelector('[data-fr-copiar]').focus();
    };

    dlg.querySelector('[data-fr-copiar]').addEventListener('click', async () => {
        try {
            await navigator.clipboard.writeText(dlg.querySelector('[data-fr-pix]').textContent);
            avisarSucesso('Código copiado. Cole no app do seu banco.');
        } catch {
            avisarErro('Não foi possível copiar. Selecione o código e copie.');
        }
    });

    dlg.querySelector('[data-fr-voltar]').addEventListener('click', () => {
        mostrar('dados');
        pagar.focus();
        avisar('O Pix gerado continua valendo: se você pagar, o relatório libera aqui.');
    });

    form.addEventListener('submit', async e => {
        e.preventDefault();
        limparErros(form);
        const erros = Object.keys(regras).map(id => [$(id), regras[id]()]);
        // O erro num campo escondido ("Alterar" fechado): os campos abrem para a pessoa ver.
        if (erros.some(([campo, texto]) => texto && campos.contains(campo))) {
            alterando = true;
            campos.hidden = false;
            resumoDados.hidden = true;
        }
        if (mostrarErros(erros)) {
            return;
        }
        await comBotao(pagar, async () => {
            try {
                // O valor do botão é o que se cobra: se o carrinho refeito mudar o total (o Pix gerado antes levou o cupom, por
                // exemplo), a pessoa confere antes de pagar.
                const totalAntes = dlg.querySelector('[data-fr-total]').textContent;
                const cartaoAntes = ehCartao();
                const cupomAntes = cupomAplicado;
                await porNoCarrinho();
                carrinhoPronto = null;
                const totalAgora = dlg.querySelector('[data-fr-total]').textContent;
                // Sem os espaços: o servidor escreve "R$ 29,90" com espaço comum e o Intl do navegador, com o inseparável.
                const semEspaco = t => t.replace(/\s/g, '');
                if (semEspaco(totalAgora) !== semEspaco(totalAntes) || ehCartao() !== cartaoAntes) {
                    mostrarErro(null, `O total do pedido agora é ${totalAgora}${cupomAntes && !cupomAplicado ? ', sem o cupom' : ''}. Confira e toque de novo para pagar.`, erroGeral);
                    return;
                }
                const comCartao = ehCartao();
                const [mes, ano] = valor('frCartVal').split('/');
                const dados = new FormData();
                dados.append('FormaPagamento', comCartao ? 'CartaoCredito' : 'Pix');
                dados.append('Cpf', valor('frPagCpf'));
                // O item da tela: a aprovação do pedido o libera mesmo com a tela fechada.
                dados.append('ItemID', dlg.dataset.item || '');
                if (comCartao) {
                    dados.append('Celular', valor('frPagCel'));
                    dados.append('CodigoPaisCelular', $('frPagCel').dataset.pais || '+55');
                    dados.append('Cep', valor('frPagCep'));
                    dados.append('Rua', valor('frPagRua'));
                    dados.append('Numero', valor('frPagNum'));
                    dados.append('Bairro', valor('frPagBairro'));
                    dados.append('Cidade', valor('frPagCidade'));
                    dados.append('Uf', valor('frPagUf'));
                    dados.append('MetodoPagamentoID', String(_bandeira(valor('frCartNum'))));
                    dados.append('CartaoNomePessoa', valor('frCartNome'));
                    dados.append('CartaoNumero', valor('frCartNum'));
                    dados.append('CartaoMesExpiracao', mes || '');
                    dados.append('CartaoAnoExpiracao', ano || '');
                    dados.append('CartaoCVV', valor('frCartCvv'));
                    dados.append('CartaoNumeroParcelas', $('frCartParc').value || '1');
                }
                const r = await enviarFormulario(urls.finalizar, dados);
                if (!r?.sucesso) {
                    mostrarErro(null, r?.mensagem || 'Não foi possível finalizar a compra. Confira os dados e tente de novo.', erroGeral);
                    return;
                }
                const d = r.dados || {};
                if (d.Falhou) {
                    if (comCartao) {
                        $('frCartCvv').value = '';
                    }
                    mostrarErro(null, comCartao ? 'O pagamento não foi aprovado. Confira os dados do cartão ou pague com Pix.' : 'Não foi possível gerar o Pix agora. Tente de novo em instantes.', erroGeral);
                    return;
                }
                if (d.AutoAprovar) {
                    confirmar();
                    return;
                }
                pedidoAtual = d.PedidoID;
                if (comCartao) {
                    mostrar('cartao', true);
                    cartoesEmAnalise.add(d.PedidoID);
                    acompanharPedido(d.PedidoID, 3000);
                    esperarCartao(d.PedidoID);
                } else {
                    await mostrarPix(d);
                    acompanharPedido(d.PedidoID, 4000);
                }
            } catch (erro) {
                mostrarErro(null, erro?.paraPessoa ? erro.message : 'Não foi possível finalizar a compra. Confira a sua internet e tente de novo.', erroGeral);
            }
        });
    });

    dlg.querySelector('[data-fr-fechar]').addEventListener('click', () => dlg.close());
    // O X, o Esc e o Voltar fecham, o clique fora não: no meio do pagamento ele perderia os dados digitados. Fechada, o Pix continua sendo
    // conferido (pagar no app e voltar abre o relatório).
    dlg.addEventListener('close', () => {
        desempilhar('v2Pagamento');
        // Os dados do cartão não ficam na página depois de fechar.
        $('frCartNum').value = '';
        $('frCartCvv').value = '';
        bandeira = null;
        if (gatilho?.isConnected) {
            gatilho.focus();
        }
    });

    // O produto da gaveta: o da página, ou o do botão que abriu (o kit da vaga ao lado da análise). Trocar refaz o carrinho na
    // hora de pagar; o Pix de antes continua sendo acompanhado.
    const resumoNome = dlg.querySelector('[data-fr-produto-nome]');
    const padrao = { id: dlg.dataset.produto, nome: resumoNome?.textContent, total: dlg.querySelector('[data-fr-total]')?.textContent, titulo: $('frPagT').textContent };
    const usarProduto = p => {
        if (p.id === dlg.dataset.produto) {
            return;
        }
        // Com um cartão ainda em análise, outra compra cobraria duas vezes (o kit já contém o relatório). O Pix não trava: pagar
        // pede outra ação da pessoa, e travar a prenderia por até uma hora.
        if (cartoesEmAnalise.size) {
            mostrarErro(null, 'O banco ainda está analisando o pagamento do cartão. Espere a resposta antes de escolher outra compra.', erroGeral);
            return;
        }
        dlg.dataset.produto = p.id;
        resumoNome.textContent = p.nome;
        $('frPagT').textContent = p.titulo || padrao.titulo;
        dlg.querySelector('[data-fr-subtotal]').textContent = p.total;
        dlg.querySelectorAll('[data-fr-total]').forEach(el => { el.textContent = p.total; });
        linhaDesconto.hidden = true;
        totalZero = false;
        $('frCartParc').replaceChildren(new Option(`1 vez de ${p.total}`, '1'));
        // Com cupom, o carrinho do produto novo diz se ele ainda vale (e o desconto aparece de novo).
        carrinhoPronto = ehCartao() || cupomAplicado ? porNoCarrinho().catch(() => { carrinhoPronto = null; }) : null;
        bandeira = null;
        pedidoAtual = null;
        ajustarForma();
        mostrar('dados');
    };

    ajustarForma();

    return botao => {
        gatilho = botao;
        if (!confirmado) {
            usarProduto(botao?.dataset.frProduto ? { id: botao.dataset.frProduto, nome: botao.dataset.frNome, total: botao.dataset.frTotal, titulo: botao.dataset.frTitulo } : padrao);
        }
        if (!confirmado && passos.find(p => !p.hidden)?.dataset.passo !== 'pix' && passos.find(p => !p.hidden)?.dataset.passo !== 'cartao') {
            mostrar('dados');
        }
        dlg.showModal();
        empilhar('v2Pagamento', () => dlg.close());
        $('frPagT').focus({ preventScroll: true });
    };
}

// ---------- simulador: pedido do treino ----------
function _treino(treino) {
    const urls = urlsDe(treino);
    const form = $('frTreinoForm');
    const arq = $('frTreinoArq');
    const link = $('frTreinoLink');
    const titulo = $('frTreinoTitulo');
    const descricao = $('frTreinoDescricao');
    const zona = treino.querySelector('[data-fr-zona]');
    const nome = treino.querySelector('[data-fr-nome]');
    const tipoArquivo = form.querySelector('[data-fr-arquivo]');
    const chips = [...form.querySelectorAll('[data-fr-vaga]')];
    let vaga = chips.find(c => c.getAttribute('aria-pressed') === 'true')?.dataset.frVaga || '';
    const radiosPersona = [...form.querySelectorAll('[name="personaID"]')];
    const avisoPersona = treino.querySelector('[data-fr-persona-aviso]');
    const rotuloTreino = treino.querySelector('[data-fr-treino-rotulo]');
    const nomePersona = radio => radio.closest('label').querySelector('b').textContent;
    let escolhaAntes = null;

    // O treino é um por vaga e volta com o entrevistador dele: na vaga que já tem um, a escolha fica nele (travada, com o motivo), e
    // sem a vaga volta ao que a pessoa tinha marcado. Escolher e ver a escolha ignorada confundia (rework #8368).
    const ajustarPersona = () => {
        const alvo = radiosPersona.find(r => r.value === chips.find(c => c.getAttribute('aria-pressed') === 'true')?.dataset.frPersona);
        if (alvo && !escolhaAntes) {
            escolhaAntes = radiosPersona.find(r => r.checked) || alvo;
        }
        if (alvo) {
            alvo.checked = true;
        } else if (escolhaAntes) {
            escolhaAntes.checked = true;
            escolhaAntes = null;
        }
        radiosPersona.forEach(r => {
            r.disabled = !!alvo && r !== alvo;
        });
        avisoPersona.hidden = !alvo;
        if (alvo) {
            avisoPersona.querySelector('p').textContent = `Você já tem um treino desta vaga com ${nomePersona(alvo)}. Ele continua de onde parou, com o mesmo entrevistador.`;
        }
        rotuloTreino.textContent = alvo ? 'Continuar o treino' : 'Começar o treino';
    };

    const atualizarZona = () => {
        zona.hidden = !tipoArquivo.checked;
    };
    form.querySelectorAll('[name="curriculoTipo"]').forEach(r => r.addEventListener('change', atualizarZona));
    arq.addEventListener('change', () => {
        nome.textContent = arq.files[0]?.name || 'Nenhum arquivo escolhido';
        mostrarErro(arq, '');
    });
    // Uma vaga por vez: a escolhida apaga as outras formas.
    const limparOutras = manter => {
        if (manter !== 'chip') {
            chips.forEach(c => c.setAttribute('aria-pressed', 'false'));
            vaga = '';
        }
        if (manter !== 'link') {
            link.value = '';
            mostrarErro(link, '');
        }
        if (manter !== 'escrever') {
            titulo.value = '';
            descricao.value = '';
            mostrarErro(titulo, '');
            mostrarErro(descricao, '');
        }
        ajustarPersona();
    };
    chips.forEach(chip => chip.addEventListener('click', () => {
        const ligar = chip.getAttribute('aria-pressed') !== 'true';
        limparOutras('chip');
        chips.forEach(c => c.setAttribute('aria-pressed', String(c === chip && ligar)));
        vaga = ligar ? chip.dataset.frVaga : '';
        ajustarPersona();
    }));
    ajustarPersona();
    link.addEventListener('input', () => {
        if (link.value.trim()) {
            limparOutras('link');
        }
    });
    [titulo, descricao].forEach(c => c.addEventListener('input', () => {
        if (c.value.trim()) {
            limparOutras('escrever');
        }
    }));

    const regras = {
        frTreinoLink: () => {
            const v = link.value.trim();
            return !v || /^https?:\/\/[^\s.]+\.[^\s]+$/i.test(v) ? '' : 'Cole o link completo da vaga, começando com https://';
        },
        frTreinoTitulo: () => (descricao.value.trim() && !titulo.value.trim() ? 'Escreva o cargo da vaga.' : ''),
        frTreinoDescricao: () => (titulo.value.trim() && descricao.value.trim().length < 30 ? 'Conte um pouco mais do que a vaga pede.' : '')
    };
    validarAoVivo(form, regras);

    form.addEventListener('submit', async e => {
        e.preventDefault();
        limparErros(form);
        const comArquivo = tipoArquivo.checked;
        const erros = Object.keys(regras).map(id => [$(id), regras[id]()]);
        if (comArquivo) {
            erros.unshift([arq, _erroArquivo(arq.files[0])]);
        }
        if (erros.some(([campo, texto]) => texto && treino.querySelector('[data-fr-escrever]').contains(campo))) {
            treino.querySelector('[data-fr-escrever]').open = true;
        }
        if (mostrarErros(erros)) {
            return;
        }

        const dados = new FormData();
        dados.append('curriculoTipo', form.querySelector('[name="curriculoTipo"]:checked')?.value || '');
        dados.append('personaID', form.querySelector('[name="personaID"]:checked')?.value || '0');
        if (comArquivo) {
            dados.append('arquivo', arq.files[0], arq.files[0].name);
        }
        if (vaga) {
            dados.append('vaga', vaga);
        } else if (link.value.trim()) {
            dados.append('vagaLink', link.value.trim());
        } else if (descricao.value.trim()) {
            dados.append('vagaTitulo', titulo.value.trim());
            dados.append('vagaDescricao', descricao.value.trim());
        }

        await comBotao(form.querySelector('[type="submit"]'), async () => {
            try {
                const r = await enviarFormulario(urls.salvar, dados);
                if (r?.sucesso && r.url) {
                    // A vaga já tinha um treino (link colado, ou a tela desatualizada): ele abre com o entrevistador dele, e a pessoa é avisada antes.
                    const mantida = r.personaMantida && radiosPersona.find(p => p.value === String(r.personaMantida));
                    if (mantida) {
                        avisoPersona.querySelector('p').textContent = `Esta vaga já tem um treino com ${nomePersona(mantida)}. Vamos abrir ele, de onde parou.`;
                        avisoPersona.hidden = false;
                        await new Promise(ok => setTimeout(ok, 2500));
                    }
                    window.location.href = r.url;
                    return true;
                }
                mostrarErro(null, r?.mensagem || 'Não foi possível começar o treino. Tente de novo.', $('frTreinoE'));
            } catch {
                mostrarErro(null, 'Não foi possível começar o treino. Confira a sua internet e tente de novo.', $('frTreinoE'));
            }
            return false;
        }, { manter: ok => ok === true });
    });
}

// ---------- simulador: a oferta depois das 2 perguntas grátis ----------
function _ofertaEntrevista(oferta) {
    const urls = urlsDe(oferta);
    const id = oferta.dataset.id;
    const continuar = async (tentativas = 1) => {
        let r = null;
        for (let i = 0; i < tentativas; i += 1) {
            const dados = new FormData();
            dados.append('id', id);
            r = await enviarFormulario(urls.continuar, dados).catch(() => null);
            if (r?.sucesso) {
                return r;
            }
            await esperar(2000);
        }
        return r;
    };

    const usar = oferta.querySelector('[data-fr-continuar]');
    usar?.addEventListener('click', () => comBotao(usar, async () => {
        const r = await continuar();
        if (r?.sucesso) {
            window.location.href = r.url || urls.entrevista;
            return true;
        }
        mostrarErro(null, r?.mensagem || 'Não foi possível continuar agora. Tente de novo.', $('frContinuarE'));
        return false;
    }, { manter: ok => ok === true }));

    const gaveta = $('frPag');
    if (gaveta) {
        const abrir = _gaveta(gaveta, {
            // Pagou: o crédito novo continua a entrevista, que segue na pergunta 3.
            aoConfirmar: async ({ texto, barra }) => {
                barra.style.width = '60%';
                const r = await continuar(5);
                if (!r?.sucesso) {
                    texto.textContent = 'O pagamento entrou, mas a entrevista ainda não continuou. Recarregue a página: o seu crédito está guardado.';
                    return;
                }
                barra.style.width = '100%';
                irPara(r.url || urls.entrevista);
            }
        });
        oferta.querySelectorAll('[data-fr-comprar]').forEach(botao => botao.addEventListener('click', () => abrir(botao)));
    }
}

// ---------- carta de apresentação: o pedido ----------
function _cartaNova(pagina) {
    const urls = urlsDe(pagina);
    const form = $('frCartaForm');
    const analise = pagina.dataset.analise || '';
    const link = $('frCartaLink');
    const titulo = $('frCartaTitulo');
    const texto = $('frCartaTexto');
    const escrever = pagina.querySelector('[data-fr-escrever]');
    const chips = [...form.querySelectorAll('[data-fr-vaga]')];
    let vaga = chips.find(c => c.getAttribute('aria-pressed') === 'true')?.dataset.frVaga || '';

    // Vale a 1ª forma preenchida, nesta ordem: a candidatura, o texto (o aviso do link de outro site manda colá-lo), o link. Nenhuma
    // apaga a outra (um texto colado de 3 mil letras não pode sumir); escrever num campo só desmarca a candidatura.
    const desmarcarCandidatura = () => {
        chips.forEach(c => c.setAttribute('aria-pressed', 'false'));
        vaga = '';
    };
    chips.forEach(chip => chip.addEventListener('click', () => {
        const ligar = chip.getAttribute('aria-pressed') !== 'true';
        chips.forEach(c => c.setAttribute('aria-pressed', String(c === chip && ligar)));
        vaga = ligar ? chip.dataset.frVaga : '';
        mostrarErro(null, '', $('frCartaVagaE'));
    }));
    [link, titulo, texto].forEach(c => c?.addEventListener('input', () => {
        if (c.value.trim()) {
            desmarcarCandidatura();
        }
    }));

    const regras = link ? {
        frCartaLink: () => {
            const v = link.value.trim();
            return !v || /^(https?:\/\/)?([^/\s]+\.)?empregare\.com(\/|$)/i.test(v) ? '' : 'Cole o link de uma vaga da Empregare. De outro site, cole o texto da vaga.';
        },
        frCartaTexto: () => {
            const v = texto.value.trim();
            return !v || v.length >= 100 ? '' : 'Cole o texto inteiro da vaga (a descrição e os requisitos).';
        }
    } : {};
    validarAoVivo(form, regras);
    const formaEnviada = () => analise || vaga ? '' : texto?.value.trim() ? 'frCartaTexto' : link?.value.trim() ? 'frCartaLink' : '';

    form.addEventListener('submit', async e => {
        e.preventDefault();
        limparErros(form);
        // Só a forma que vai ser enviada barra o envio: o link de outro site não trava o texto colado depois dele.
        const forma = formaEnviada();
        const erros = Object.keys(regras).map(campo => [$(campo), campo === forma ? regras[campo]() : '']);
        if (erros.some(([campo, msg]) => msg && escrever?.contains(campo))) {
            escrever.open = true;
        }
        if (mostrarErros(erros)) {
            return;
        }
        const dados = new FormData();
        if (analise) {
            dados.append('analise', analise);
        } else if (vaga) {
            dados.append('vaga', vaga);
        } else if (forma === 'frCartaTexto') {
            dados.append('vagaTitulo', titulo.value.trim());
            dados.append('vagaTexto', texto.value.trim());
        } else if (forma === 'frCartaLink') {
            dados.append('vagaLink', link.value.trim());
        } else {
            mostrarErro(null, 'Escolha a vaga da carta: uma candidatura, o link de uma vaga da Empregare ou o texto da vaga.', $('frCartaVagaE'));
            return;
        }

        await comBotao(form.querySelector('[type="submit"]'), async () => {
            try {
                const r = await enviarFormulario(urls.salvar, dados);
                if (r?.sucesso && r.url) {
                    window.location.href = r.url;
                    return true;
                }
                mostrarErro(null, r?.mensagem || 'Não foi possível escrever a carta. Tente de novo.', $('frCartaE'));
            } catch {
                mostrarErro(null, 'Não foi possível escrever a carta. Confira a sua internet e tente de novo.', $('frCartaE'));
            }
            return false;
        }, { manter: ok => ok === true });
    });
}

// ---------- carta de apresentação: a carta, a oferta e os ajustes ----------
function _carta(carta) {
    const urls = urlsDe(carta);
    const id = carta.dataset.id;

    carta.querySelectorAll('[data-fr-liberar]').forEach(botao => botao.addEventListener('click', () => comBotao(botao, async () => {
        try {
            const r = await _liberar(urls, id);
            if (r?.sucesso) {
                window.location.href = `${window.location.pathname}?liberada=1`;
                return true;
            }
            mostrarErro(null, r?.mensagem || 'Não foi possível liberar agora. Tente de novo.', $('frLiberarE'));
        } catch {
            mostrarErro(null, 'Não foi possível liberar agora. Confira a sua internet e tente de novo.', $('frLiberarE'));
        }
        return false;
    }, { manter: ok => ok === true })));

    const regerar = carta.querySelector('[data-fr-regerar]');
    regerar?.addEventListener('click', () => comBotao(regerar, async () => {
        const dados = new FormData();
        dados.append('id', id);
        dados.append('tom', carta.querySelector('[name="frTom"]:checked')?.value || '0');
        try {
            const r = await enviarFormulario(urls.regerar, dados);
            if (r?.sucesso) {
                window.location.href = `${window.location.pathname}?escrita=1`;
                return true;
            }
            mostrarErro(null, r?.mensagem || 'Não foi possível escrever a carta agora. Tente de novo.', $('frRegerarE'));
        } catch {
            mostrarErro(null, 'Não foi possível escrever a carta agora. Confira a sua internet e tente de novo.', $('frRegerarE'));
        }
        return false;
    }, { manter: ok => ok === true }));

    _ligarApagar(carta, urls.excluir, id);
    _ligarCopiar(carta);

    const gaveta = $('frPag');
    if (gaveta) {
        const abrir = _gaveta(gaveta, {
            // Pagou: o crédito novo libera a carta, que já está escrita.
            aoConfirmar: async ({ texto, barra }) => {
                barra.style.width = '60%';
                const r = await _liberar(urls, id, 5).catch(() => null);
                if (!r?.sucesso) {
                    texto.textContent = 'O pagamento entrou, mas a carta ainda não abriu. Recarregue a página: o seu crédito está guardado.';
                    return;
                }
                barra.style.width = '100%';
                irPara(`${window.location.pathname}?pago=1`);
            }
        });
        carta.querySelectorAll('[data-fr-comprar]').forEach(botao => botao.addEventListener('click', () => abrir(botao)));
    }
}

// ---------- aconselhamento de carreira: o questionário ----------
// Uma pergunta por tela; cada uma é conferida antes de seguir (o servidor confere de novo). A IA leva até um minuto: a tela de
// espera fica no lugar do formulário, e um erro devolve a pessoa à última pergunta com as respostas.
function _carreiraNova(pagina) {
    const urls = urlsDe(pagina);
    const form = $('frCarreiraForm');
    const perguntas = [...form.querySelectorAll('[data-pergunta]')];
    const progresso = form.querySelector('[data-fr-progresso]');
    const contador = form.querySelector('[data-fr-contador]');
    const voltar = form.querySelector('[data-fr-voltar]');
    const continuar = form.querySelector('[data-fr-continuar]');
    const enviar = form.querySelector('[data-fr-enviar]');
    const espera = $('frCarreiraEspera');
    const corrigir = form.querySelector('[data-fr-corrigir]');
    let atual = 0;

    const marcados = (raiz, nome) => [...raiz.querySelectorAll(`[name="${nome}"]:checked`)];
    const objetivo = () => form.querySelector('[name="Objetivo"]:checked')?.value || '';
    const erroDe = p => p.querySelector(':scope > .campo-erro');

    // Nas de várias escolhas com limite, as caixas que sobram travam ao chegar nele.
    form.querySelectorAll('[data-maximo]').forEach(grupo => {
        const maximo = Number(grupo.dataset.maximo);
        const caixas = [...grupo.querySelectorAll('input[type="checkbox"]')];
        if (maximo < 2 || !caixas.length) {
            return;
        }
        const travar = () => {
            const n = caixas.filter(c => c.checked).length;
            caixas.forEach(c => { c.disabled = !c.checked && n >= maximo; });
        };
        caixas.forEach(c => c.addEventListener('change', travar));
    });
    form.querySelectorAll('[name="ConfirmouCurriculo"][type="radio"]').forEach(r => r.addEventListener('change', () => {
        corrigir.hidden = form.querySelector('[name="ConfirmouCurriculo"]:checked')?.value !== 'false';
    }));

    // A pergunta 10 conforme a 1: só a parte do objetivo aparece, e o nome dela é o que o leitor de tela anuncia.
    const prepararDez = p => {
        p.querySelectorAll('[data-objetivo]').forEach(parte => { parte.hidden = parte.dataset.objetivo !== objetivo(); });
        const titulo = p.querySelector('[data-objetivo]:not([hidden]) .fr-pergunta__t');
        if (titulo) {
            p.setAttribute('aria-labelledby', titulo.id);
        }
    };

    // [campo, texto]: o campo é o select do passo 2 (erro ligado a ele); nas perguntas de botão, o erro é do grupo.
    const conferir = p => {
        const n = Number(p.dataset.pergunta);
        if (n === 1) {
            return [null, objetivo() ? '' : 'Escolha o que você quer resolver.'];
        }
        if (n === 2) {
            const confirmou = form.querySelector('[name="ConfirmouCurriculo"]:checked, input[type="hidden"][name="ConfirmouCurriculo"]')?.value;
            if (confirmou === undefined) {
                return [null, 'Diga se está certo ou corrija.'];
            }
            if (confirmou === 'false' && !$('frCarEsc').value) {
                return [$('frCarEsc'), 'Escolha a sua escolaridade.'];
            }
            return confirmou === 'false' && !$('frCarEst').value ? [$('frCarEst'), 'Escolha o estado onde você mora.'] : [null, ''];
        }
        if (n === 10) {
            if (objetivo() === '2' && !marcados(p, 'Incomoda').length) {
                return [null, 'Escolha pelo menos uma coisa que incomoda hoje (até 2).'];
            }
            return [null, objetivo() === '3' && !marcados(p, 'TopaMudar').length ? 'Diga se topa mudar de área.' : ''];
        }
        const minimo = Number(p.dataset.minimo || 0);
        const nome = n === 12 ? 'Seguranca' : p.querySelector('input[type="radio"], input[type="checkbox"]')?.name;
        if (n === 12 || (minimo && nome)) {
            return [null, marcados(p, nome).length ? '' : n === 12 ? 'Escolha de 1 a 5.' : 'Escolha pelo menos uma opção.'];
        }
        return [null, ''];
    };

    // O erro no padrão dos formulários v2: no campo (aria-invalid e borda) ou no grupo de botões (borda nos botões).
    const marcarErro = p => {
        const [campo, texto] = conferir(p);
        [$('frCarEsc'), $('frCarEst')].forEach(s => { if (p.contains(s)) { mostrarErro(s, ''); } });
        if (campo) {
            mostrarErro(campo, texto);
        } else {
            mostrarErro(null, texto, erroDe(p));
        }
        p.querySelectorAll('.fr-opcoes, .fr-escala').forEach(g => g.classList.toggle('is-err', !!texto && !campo));
        return [campo, texto];
    };
    // Com o erro na tela, ele some (ou muda) a cada resposta; o mesmo erro não é lido de novo pelo leitor de tela.
    perguntas.forEach(p => p.addEventListener('change', () => {
        const aviso = erroDe(p);
        if (aviso && !aviso.hidden && conferir(p)[1] !== aviso.textContent.trim()) {
            marcarErro(p);
        }
    }));

    const mostrar = (i, focar = true) => {
        atual = i;
        const p = perguntas[i];
        if (p.dataset.pergunta === '10') {
            prepararDez(p);
        }
        perguntas.forEach((q, k) => { q.hidden = k !== i; });
        progresso.style.width = `${Math.round(((i + 1) / perguntas.length) * 100)}%`;
        contador.textContent = `Pergunta ${i + 1} de ${perguntas.length}`;
        voltar.hidden = i === 0;
        continuar.hidden = i === perguntas.length - 1;
        enviar.hidden = i !== perguntas.length - 1;
        if (focar) {
            p.focus();
        }
    };

    const seguir = () => {
        const [campo, erro] = marcarErro(perguntas[atual]);
        if (erro) {
            campo?.focus();
            return false;
        }
        if (atual < perguntas.length - 1) {
            mostrar(atual + 1);
            history.pushState({ ...(history.state || {}), frPasso: atual }, '');
        }
        return true;
    };
    continuar.addEventListener('click', seguir);
    // Cada pergunta é uma entrada no histórico (como no Fale conosco): o Voltar do navegador ou do celular volta uma pergunta,
    // sem sair da página nem perder as respostas; o "Voltar" da tela faz o mesmo.
    voltar.addEventListener('click', () => {
        if (history.state?.frPasso > 0) {
            history.back();
        } else if (atual > 0) {
            mostrar(atual - 1);
        }
    });
    window.addEventListener('popstate', e => {
        const passo = Math.min(e.state?.frPasso || 0, perguntas.length - 1);
        // Na espera do envio, ou no fechar do menu do topo (o mesmo passo), nada muda.
        if (!form.hidden && passo !== atual) {
            mostrar(passo);
        }
    });
    // Enviado: as entradas das perguntas saem do histórico antes do resultado, que fica no lugar da primeira (o Voltar dele
    // volta para antes do questionário, não para a pergunta 12).
    const irAoResultado = destino => {
        const n = history.state?.frPasso || 0;
        if (!n) {
            window.location.replace(destino);
            return;
        }
        window.addEventListener('popstate', () => window.location.replace(destino), { once: true });
        history.go(-n);
    };
    // Enter num campo de texto segue para a próxima pergunta (sem ele, enviaria o questionário pela metade).
    form.addEventListener('keydown', e => {
        if (e.key === 'Enter' && e.target.matches('input') && atual < perguntas.length - 1) {
            e.preventDefault();
            seguir();
        }
    });

    const erroGeral = $('frCarreiraE');
    const voltarAoFormulario = texto => {
        espera.hidden = true;
        form.hidden = false;
        mostrarErro(null, texto, erroGeral);
        enviar.focus();
    };
    form.addEventListener('submit', async e => {
        e.preventDefault();
        if (atual !== perguntas.length - 1 || !seguir()) {
            return;
        }
        mostrarErro(null, '', erroGeral);
        const dados = new FormData(form);
        await comBotao(enviar, async () => {
            form.hidden = true;
            espera.hidden = false;
            espera.querySelector('h1').focus();
            const lista = espera.querySelector('[data-fr-etapas]');
            const status = espera.querySelector('[data-fr-status]');
            let feitas = 0;
            const relogio = setInterval(() => {
                if (feitas < 2) {
                    feitas += 1;
                    _marcarEtapas(lista, feitas, feitas);
                    // A troca da bolinha não é lida pelo leitor de tela; a etapa nova vai para o role="status".
                    status.textContent = `${lista.children[feitas].textContent.trim()}…`;
                }
            }, 12000);
            try {
                const r = await enviarFormulario(urls.salvar, dados);
                if (r?.sucesso && r.url) {
                    _marcarEtapas(lista, 3, -1);
                    irAoResultado(r.url);
                    return true;
                }
                voltarAoFormulario(r?.mensagem || 'Não foi possível montar os seus caminhos agora. Tente de novo.');
            } catch {
                voltarAoFormulario('Não foi possível montar os seus caminhos. Confira a sua internet e tente de novo.');
            } finally {
                clearInterval(relogio);
            }
            return false;
        }, { manter: ok => ok === true });
    });

    mostrar(0, false);
}

// ---------- aconselhamento de carreira: o resultado ----------
function _carreira(pagina) {
    const urls = urlsDe(pagina);
    const id = pagina.dataset.id;
    // O caminho escolhido ao abrir o plano: o plano dele é escrito logo depois de liberar.
    let escolhido = '0';

    const escreverPasso = caminho => {
        const dados = new FormData();
        dados.append('id', id);
        dados.append('caminho', caminho);
        return enviarFormulario(urls.passo, dados);
    };
    const abrirPasso = caminho => irPara(`${window.location.pathname}?caminho=${caminho}#frPasso${caminho}`);

    pagina.querySelectorAll('[data-fr-passo]').forEach(botao => botao.addEventListener('click', () => comBotao(botao, async () => {
        const erro = botao.parentElement.querySelector('.campo-erro');
        try {
            const r = await escreverPasso(botao.dataset.frPasso);
            if (r?.sucesso) {
                abrirPasso(botao.dataset.frPasso);
                return true;
            }
            mostrarErro(null, r?.mensagem || 'Não foi possível montar o plano agora. Tente de novo.', erro);
        } catch {
            mostrarErro(null, 'Não foi possível montar o plano. Confira a sua internet e tente de novo.', erro);
        }
        return false;
    }, { manter: ok => ok === true })));

    // Com crédito, o botão libera na hora e já escreve o plano do caminho.
    pagina.querySelectorAll('[data-fr-liberar]').forEach(botao => botao.addEventListener('click', () => comBotao(botao, async () => {
        const erro = botao.parentElement.querySelector('.campo-erro');
        try {
            const r = await _liberar(urls, id);
            if (!r?.sucesso) {
                mostrarErro(null, r?.mensagem || 'Não foi possível liberar agora. Tente de novo.', erro);
                return false;
            }
            await escreverPasso(botao.dataset.frCaminho).catch(() => null);
            abrirPasso(botao.dataset.frCaminho);
            return true;
        } catch {
            mostrarErro(null, 'Não foi possível liberar agora. Confira a sua internet e tente de novo.', erro);
        }
        return false;
    }, { manter: ok => ok === true })));

    const gaveta = $('frPag');
    if (gaveta) {
        const abrir = _gaveta(gaveta, {
            // Pagou: o crédito novo libera, e o plano do caminho escolhido é escrito antes de abrir.
            aoConfirmar: async ({ texto, barra }) => {
                barra.style.width = '40%';
                const r = await _liberar(urls, id, 5).catch(() => null);
                if (!r?.sucesso) {
                    texto.textContent = 'O pagamento entrou, mas o plano ainda não abriu. Recarregue a página: o seu crédito está guardado.';
                    return;
                }
                barra.style.width = '70%';
                await escreverPasso(escolhido).catch(() => null);
                barra.style.width = '100%';
                abrirPasso(escolhido);
            }
        });
        pagina.querySelectorAll('[data-fr-comprar]').forEach(botao => botao.addEventListener('click', () => {
            escolhido = botao.dataset.frCaminho || '0';
            abrir(botao);
        }));
    }

    pagina.querySelector('[data-fr-imprimir]')?.addEventListener('click', () => window.print());
    _ligarApagar(pagina, urls.excluir, id);
    _abas(pagina, 'caminho');
}

// ---------- plano de ação: o "feito" de cada ação e o lembrete semanal ----------
function _planoAcao(plano) {
    const urls = urlsDe(plano);
    const progTexto = plano.querySelector('[data-pa-prog-t]');
    const progBarra = plano.querySelector('[data-pa-prog-b]');
    // Só a mensagem do servidor vai para a pessoa; "HTTP 400" e falha de rede viram o texto genérico, e o 401 já abre o aviso de sessão.
    const recusa = resposta => Object.assign(new Error(resposta?.mensagem || ''), { paraPessoa: !!resposta?.mensagem });
    const avisarFalha = e => {
        if (e?.status !== 401) {
            avisarErro(e?.paraPessoa ? e.message : 'Não foi possível salvar agora. Tente de novo.');
        }
    };

    const atualizarProgresso = resposta => {
        if (progTexto && progBarra) {
            progTexto.textContent = `${resposta.feitas} de ${resposta.total} ações feitas`;
            progBarra.style.width = `${resposta.total ? Math.round(resposta.feitas * 100 / resposta.total) : 0}%`;
            progBarra.parentElement.setAttribute('aria-valuenow', resposta.feitas);
            progBarra.parentElement.setAttribute('aria-valuemax', resposta.total);
        }
    };
    // O "Ações feitas (N)" conta só as marcadas: desmarcar uma delas também muda o número.
    const contarFeitas = () => {
        const contador = plano.querySelector('[data-pa-feitas-n]');
        if (contador) {
            contador.textContent = String(plano.querySelectorAll('.pa-feitas .pa-acao--feita').length);
        }
    };

    // Excluir uma ação feita: ela sai da lista e da conta; o foco vai à ação seguinte (ou à anterior).
    plano.querySelectorAll('[data-pa-excluir]').forEach(botao => botao.addEventListener('click', () => comBotao(botao, async () => {
        const acao = botao.closest('.pa-acao');
        const caixa = acao.querySelector('[data-pa-feito]');
        const dados = new FormData();
        dados.append('origem', botao.dataset.origem);
        dados.append('id', botao.dataset.id);
        dados.append('chave', botao.dataset.chave);
        // Desmarcar durante a exclusão faria as duas brigarem pela mesma linha.
        caixa.disabled = true;
        try {
            const resposta = await enviarFormulario(urls.excluir, dados);
            if (!resposta?.sucesso) {
                throw recusa(resposta);
            }
            // O plano zerou: a página volta pelo servidor, com o aviso de plano vazio.
            if (!resposta.total) {
                window.location.reload();
                return true;
            }
            const proxima = acao.nextElementSibling || acao.previousElementSibling;
            const lista = acao.parentElement;
            acao.remove();
            contarFeitas();
            // A seção (o grupo, o "Faça agora" ou as feitas) que ficou sem ação sai junto.
            if (!lista.children.length) {
                lista.closest('section, details')?.remove();
            }
            atualizarProgresso(resposta);
            avisarSucesso('Ação excluída do plano.');
            (proxima?.isConnected && proxima.querySelector('input, a, button') || plano.querySelector('h1'))?.focus();
            return true;
        } catch (e) {
            caixa.disabled = false;
            avisarFalha(e);
        }
        return false;
    }, { manter: ok => ok === true })));

    // A marcação vale na hora; se o servidor recusar, a caixa volta como estava.
    plano.querySelectorAll('[data-pa-feito]').forEach(caixa => caixa.addEventListener('change', async () => {
        const dados = new FormData();
        dados.append('origem', caixa.dataset.origem);
        dados.append('id', caixa.dataset.id);
        dados.append('chave', caixa.dataset.chave);
        dados.append('feito', String(caixa.checked));
        caixa.disabled = true;
        try {
            const resposta = await enviarFormulario(urls.feito, dados);
            if (!resposta?.sucesso) {
                throw recusa(resposta);
            }
            caixa.closest('.pa-acao')?.classList.toggle('pa-acao--feita', caixa.checked);
            contarFeitas();
            atualizarProgresso(resposta);
        } catch (e) {
            caixa.checked = !caixa.checked;
            avisarFalha(e);
        } finally {
            caixa.disabled = false;
            caixa.focus();
        }
    }));

    const lembrete = plano.querySelector('[data-pa-lembrete]');
    lembrete?.addEventListener('change', async () => {
        const dados = new FormData();
        dados.append('ligado', String(lembrete.checked));
        lembrete.disabled = true;
        try {
            const resposta = await enviarFormulario(urls.lembrete, dados);
            if (!resposta?.sucesso) {
                throw recusa(resposta);
            }
            avisarSucesso(lembrete.checked ? 'Pronto: toda segunda-feira você recebe as próximas ações por e-mail.' : 'Lembrete desligado.');
        } catch (e) {
            lembrete.checked = !lembrete.checked;
            avisarFalha(e);
        } finally {
            lembrete.disabled = false;
            lembrete.focus();
        }
    });
}

// ---------- páginas públicas: o caminho de quem já entrou ----------
function _linksDeQuemEntrou(pagina) {
    const faixa = pagina.querySelector('[data-fr-eu]');
    obterUsuario().then(u => {
        if (!u?.logado) {
            // O lugar que o <head> reservou pela marca de quem entrou antes (sessão vencida) sai.
            faixa?.remove();
            return;
        }
        pagina.querySelectorAll('[data-fr-href-logado]').forEach(a => { a.href = a.dataset.frHrefLogado; });
        pagina.querySelectorAll('[data-fr-so-visitante]').forEach(el => { el.hidden = true; });
        _faixaDaConta(faixa);
    }).catch(() => { faixa?.remove(); });
}

// A faixa "na sua conta" aparece já com o texto genérico do HTML e recebe o último uso desta ferramenta e os créditos dela.
// Sem nada (ou sem resposta), fica o genérico, com o botão para Minhas ferramentas: a altura não muda e a página não pula.
async function _faixaDaConta(faixa) {
    if (!faixa) {
        return;
    }
    faixa.hidden = false;
    const acao = faixa.querySelector('[data-fr-eu-acao]');
    try {
        const resumo = await obterJson(faixa.dataset.frEu, { semCache: true, silencioso: true });
        if (resumo?.tem) {
            faixa.querySelector('[data-fr-eu-t]').textContent = resumo.titulo;
            faixa.querySelector('[data-fr-eu-d]').textContent = resumo.detalhe || '';
            acao.textContent = resumo.acao;
            if (resumo.url) {
                acao.href = resumo.url;
            }
            return;
        }
    } catch {
        // Fica o genérico.
    }
    acao.textContent = 'Minhas ferramentas';
    acao.removeAttribute('aria-describedby');
    faixa.querySelector('.fp-eu__link')?.remove();
}

// ---------- início ----------
const treino = $('frTreino');
if (treino) {
    _treino(treino);
}
const ofertaEntrevista = $('frOfertaEnt');
if (ofertaEntrevista) {
    _ofertaEntrevista(ofertaEntrevista);
}
// O HTML das páginas públicas é o mesmo para todos (cache público): quem entrou vai direto ao pedido.
document.querySelectorAll('[data-fr-pub]').forEach(_linksDeQuemEntrou);
const planoAcao = $('paPlano');
if (planoAcao) {
    _planoAcao(planoAcao);
}
const pub = $('frPub');
if (pub) {
    _paginaPublica(pub);
}
const nova = $('frNova');
if (nova) {
    _pedido(nova);
}
const hub = $('frHub');
// As ferramentas ficam empilhadas: o ?aba= dos links de antes (e-mails, volta do pagamento) rola até a seção dela.
const secaoDoHub = hub?.dataset.frIr && $(`frSec-${hub.dataset.frIr}`);
if (secaoDoHub) {
    secaoDoHub.scrollIntoView({ block: 'start' });
    // O foco vai junto: o leitor de tela começa pela ferramenta que o link pediu.
    const tituloDaSecao = secaoDoHub.querySelector('h2');
    tituloDaSecao?.setAttribute('tabindex', '-1');
    tituloDaSecao?.focus({ preventScroll: true });
}
const rel = $('frRel');
if (rel) {
    _relatorio(rel);
}
const cartaNova = $('frCartaNova');
if (cartaNova) {
    _cartaNova(cartaNova);
}
const carta = $('frCarta');
if (carta) {
    _carta(carta);
}
const relSimulador = $('frRelSim');
if (relSimulador) {
    _ligarPdf(relSimulador);
    _contarNota(relSimulador);
}
const carreiraNova = $('frCarreiraNova');
if (carreiraNova) {
    _carreiraNova(carreiraNova);
}
const carreira = $('frCarreira');
if (carreira) {
    _carreira(carreira);
}
// A busca na lista de candidaturas dos pedidos: pelo cargo ou pela empresa, sem acento. A lista só abre ao focar ou digitar e fecha ao
// escolher, no Esc ou ao sair; a vaga escolhida passa para o campo (com o termo digitado ali e a lista aberta, ninguém sabia se a
// vaga tinha sido escolhida). A marcada nunca some da lista (é a que vai no pedido).
document.querySelectorAll('[data-fr-vagas-busca]').forEach((busca) => {
    const caixa = busca.closest('[data-fr-vagas]');
    const itens = [...caixa.querySelectorAll('[data-fr-vaga]')];
    const nada = caixa.querySelector('[data-fr-vagas-nada]');
    const lista = caixa.querySelector('.fr-vagas__lista');
    let semAbrir = false; // o foco devolvido ao campo (Esc, Enter no item) não pode reabrir a lista
    const aberta = () => caixa.classList.contains('is-aberta');
    const marcada = () => itens.find(i => i.getAttribute('aria-pressed') === 'true');
    const rotulo = () => [...(marcada()?.querySelectorAll('b, span') ?? [])].map(e => e.textContent.trim()).filter(Boolean).join(' · ');
    const filtrar = termo => {
        const bate = i => !termo || semAcento(i.dataset.frVagaNome).includes(termo);
        itens.forEach(i => (i.hidden = !bate(i) && i.getAttribute('aria-pressed') !== 'true'));
        nada.hidden = itens.some(bate);
        lista.hidden = itens.every(i => i.hidden);
    };
    // Aberta, o campo é de busca (começa vazio e mostra tudo); fechada, mostra a vaga escolhida.
    const abrir = limpar => {
        if (!aberta()) {
            caixa.classList.add('is-aberta');
            if (limpar) busca.value = '';
        }
        filtrar(semAcento(busca.value.trim()));
    };
    const fechar = () => {
        caixa.classList.remove('is-aberta');
        busca.value = rotulo();
        filtrar('');
    };
    const devolverFoco = () => {
        semAbrir = true;
        busca.focus({ preventScroll: true });
        semAbrir = false;
    };

    busca.addEventListener('focus', () => !aberta() && !semAbrir && abrir(true));
    busca.addEventListener('click', () => !aberta() && abrir(true));
    busca.addEventListener('input', () => abrir(false));
    // O clique do teclado (detail 0) devolve o foco ao campo; no toque não: o teclado do celular abriria por cima da lista.
    itens.forEach(i => i.addEventListener('click', e => setTimeout(() => {
        fechar();
        if (e.detail === 0) devolverFoco();
    })));
    caixa.addEventListener('keydown', e => {
        // O Enter na busca enviaria o pedido.
        if (e.key === 'Enter' && e.target === busca) {
            e.preventDefault();
        } else if (e.key === 'Escape' && aberta()) {
            fechar();
            devolverFoco();
            busca.select();
        } else if ((e.key === 'ArrowDown' || e.key === 'ArrowUp') && !aberta() && e.target === busca) {
            e.preventDefault();
            abrir(true);
        } else if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
            const ordem = [busca, ...itens.filter(i => !i.hidden)];
            const atual = ordem.indexOf(document.activeElement);
            if (atual < 0) return;
            e.preventDefault();
            ordem[Math.max(0, Math.min(ordem.length - 1, atual + (e.key === 'ArrowDown' ? 1 : -1)))].focus();
        }
    });
    document.addEventListener('pointerdown', e => aberta() && !caixa.contains(e.target) && fechar());
    document.addEventListener('focusin', e => aberta() && !caixa.contains(e.target) && fechar());
    // Quem desmarca a vaga por fora (colar o link, escrever a vaga) também esvazia o campo.
    new MutationObserver(() => !aberta() && (busca.value = rotulo()))
        .observe(lista, { subtree: true, attributes: true, attributeFilter: ['aria-pressed'] });
    fechar();
});
// A lateral presa mais alta que a janela: o top negativo a deixa rolar com a página até o fim (o próximo passo) aparecer.
document.querySelectorAll('.fr-lado--preso').forEach((lado) => {
    const minimo = () => Math.min(88, window.innerHeight - lado.offsetHeight - 16);
    const ajustar = () => lado.style.setProperty('--fr-lado-top', `${minimo()}px`);
    new ResizeObserver(ajustar).observe(lado);
    window.addEventListener('resize', ajustar, { passive: true });
    // O Tab num controle escondido sob o topo do site (ou abaixo da janela) move a lateral presa até ele aparecer; ao sair, ela volta.
    lado.addEventListener('focusin', e => {
        const r = e.target.getBoundingClientRect();
        const desvio = r.top < 88 ? 88 - r.top : Math.min(0, window.innerHeight - 16 - r.bottom);
        if (desvio) {
            const atual = parseFloat(getComputedStyle(lado).top) || 0;
            lado.style.setProperty('--fr-lado-top', `${Math.max(minimo(), Math.min(88, atual + desvio))}px`);
            // No fim da página a lateral está no fim do bloco, não presa: aí quem anda é a página.
            const r2 = e.target.getBoundingClientRect();
            const falta = r2.top < 88 ? r2.top - 88 : Math.max(0, r2.bottom - (window.innerHeight - 16));
            if (falta) {
                window.scrollBy(0, falta);
            }
        }
    });
    lado.addEventListener('focusout', e => {
        if (!lado.contains(e.relatedTarget)) {
            ajustar();
        }
    });
});
